import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Star } from "lucide-react";
import { fetchAffiliates, affiliateGoUrl, formatApiError } from "../lib/api";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";

const CATEGORIES = [
  { slug: "hotels", label: "Hotels & Stays", copy: "Where we've booked, and what kept us there." },
  { slug: "airlines", label: "Flights", copy: "The engines we search first for every trip." },
  { slug: "cruises", label: "Cruises", copy: "Small ships, river cruises, and the Mediterranean we love." },
  { slug: "credit-cards", label: "Travel Cards", copy: "Points, miles, and the card that earned our last flight." },
];

export default function TravelTools() {
  const [active, setActive] = useState(CATEGORIES[0].slug);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAffiliates()
      .then((items) => {
        if (!alive) return;
        setAll(items);
        setError(null);
      })
      .catch((e) => alive && setError(formatApiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const grouped = useMemo(() => {
    const list = all.filter((a) => a.category === active);
    return [...list].sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return (a.order ?? 99) - (b.order ?? 99);
    });
  }, [all, active]);

  const activeCat = CATEGORIES.find((c) => c.slug === active);

  return (
    <main data-testid="travel-tools-page" className="bg-[#FDFBF7]">
      <SEO
        title="Travel Tools — Our Trusted Partners"
        description="The booking engines, airlines, cruise lines, and travel credit cards the Globezing team actually uses. Curated, honest, and affiliate-supported."
        path="/travel-tools"
      />

      <section className="pt-36 pb-14 lg:pt-44 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="kicker">Travel Tools</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] mt-5 max-w-4xl">
            The partners we <em className="italic">actually</em> use.
          </h1>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
            <p className="text-stone-600 max-w-2xl text-[15px] sm:text-base leading-relaxed">
              These are hand-picked booking engines, airlines, cruise lines, and credit cards we keep coming back to. When you click through, we earn a small commission at no extra cost to you — it keeps the blog running, and we only recommend what we'd use ourselves.
            </p>
            <ShareButtons title="Travel Tools — Globezing" description="Curated partners for every trip." />
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="sticky top-[72px] z-30 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-x-8 gap-y-3 py-5">
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                type="button"
                data-testid={`tools-tab-${c.slug}`}
                onClick={() => setActive(c.slug)}
                className={`text-[13px] tracking-[0.06em] transition-colors pb-1 border-b ${
                  active === c.slug
                    ? "text-[#9c4122] border-[#9c4122] font-semibold"
                    : "text-stone-600 border-transparent hover:text-stone-900"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="kicker text-stone-500 mb-10">{activeCat?.copy}</p>

          {loading ? (
            <div className="flex items-center gap-3 text-stone-500" data-testid="tools-loading">
              <Loader2 className="animate-spin" size={16} /> Loading partners…
            </div>
          ) : error ? (
            <div data-testid="tools-error" className="py-20 border border-dashed border-stone-300 text-center">
              <p className="text-stone-700">Couldn't load partners.</p>
              <p className="text-sm text-stone-500 mt-2">{error}</p>
            </div>
          ) : grouped.length === 0 ? (
            <div data-testid="tools-empty" className="py-20 border border-dashed border-stone-300 text-center">
              <p className="text-stone-700">Nothing curated here yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
              {grouped.map((a, i) => (
                <AffiliateCard key={a.slug} a={a} index={i} />
              ))}
            </div>
          )}

          <p className="mt-16 text-xs text-stone-500 max-w-2xl leading-relaxed">
            Disclosure: some links on this page are affiliate links. If you make a booking or open an account through them, Globezing may earn a commission at no additional cost to you. We only recommend products and services we've personally used or researched thoroughly.
          </p>
        </div>
      </section>
    </main>
  );
}

function LogoOrFallback({ a, size = "lg" }) {
  const [failed, setFailed] = React.useState(false);
  const box =
    size === "sm"
      ? "w-14 h-14"
      : size === "md"
      ? "w-9 h-9"
      : "w-20 h-20";
  const letter = size === "md" ? "text-xl" : size === "sm" ? "text-xl" : "text-3xl";
  const showImg = a.logo && !failed;
  return (
    <div className={`${box} flex-shrink-0 rounded-sm bg-stone-50 border border-stone-100 flex items-center justify-center overflow-hidden`}>
      {showImg ? (
        <img
          src={a.logo}
          alt={a.name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="max-w-[75%] max-h-[75%] object-contain"
        />
      ) : (
        <span className={`font-serif ${letter} text-stone-400`}>{a.name[0]}</span>
      )}
    </div>
  );
}

function AffiliateCard({ a, index }) {
  const href = affiliateGoUrl(a.slug);
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener nofollow sponsored"
      data-testid={`affiliate-card-${a.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      className="group block border border-stone-200 bg-white/60 hover:bg-white hover:border-stone-400 transition-all p-6 sm:p-7"
    >
      <div className="flex items-start gap-5">
        <LogoOrFallback a={a} size="lg" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {a.featured && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#9c4122] font-semibold">
                <Star size={11} className="fill-current" /> Top pick
              </span>
            )}
          </div>
          <h3 className="font-serif text-2xl mt-1 text-stone-900 group-hover:text-[#9c4122] transition-colors">
            {a.name}
          </h3>
          {a.highlight && (
            <p className="mt-1.5 text-[12px] tracking-[0.15em] uppercase text-stone-500 font-medium">
              {a.highlight}
            </p>
          )}
          <p className="mt-3 text-stone-600 text-[14px] leading-relaxed">{a.pitch}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold text-stone-800 group-hover:text-[#9c4122]">
            Visit {a.name} <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </div>
    </motion.a>
  );
}
